import { cloneDeep } from 'lodash'
import { dict } from '@/api/registration'
import { dictWithUrl } from '@/components/api/api'
import config from '@/config/user.env'

export class DcDic {
    static cachedDicMap = new Map()

    dict
    dictInverse
    options

    constructor(dict, code) {
        if (Array.isArray(dict)) {
            this.options = dict
            this.dict = {}
            this.dictInverse = {}
            for (const o of dict) {
                this.dict[o.value] = o.name
                this.dictInverse[o.name] = o.value
            }
        } else if (dict && typeof dict === 'object') {
            this.dict = dict
            this.dictInverse = {}
            this.options = []
            for (const [value, name] of Object.entries(dict)) {
                this.dictInverse[name] = value
                this.options.push({
                    value,
                    name,
                })
            }
        } else {
            throw Error(`字典[${code}] 构造失败`)
        }
    }

    static async fetchDic(dicCode, force = false) {
        if (force) {
            return (await dict(config.dcDictPath, dicCode, true)).data[dicCode]
        } else {
            return (await dictWithUrl(dicCode)).data[dicCode]
        }
    }

    static async createMult(dictCodes, force = false) {
        let _dictCodes = dictCodes
        if (typeof dictCodes === 'string') {
            _dictCodes = dictCodes.split(',')
        }

        return Promise.all(
            _dictCodes.map(async code => {
                if (typeof code !== 'string') {
                    return DcDic.create(code, force)
                }

                if (!force && DcDic.cachedDicMap.has(code)) {
                    return DcDic.cachedDicMap.get(code)
                }

                const dicObj = await DcDic.fetchDic(code, force)
                const dic = new DcDic(dicObj, code)
                dic.code = code
                DcDic.cachedDicMap.set(code, dic)
                return dic
            }),
        )
    }

    static async create(DicCtor, force = false) {
        if (!force && DcDic.cachedDicMap.has(DicCtor)) {
            return DcDic.cachedDicMap.get(DicCtor)
        }

        const dicObj = await DcDic.fetchDic(DicCtor.code, force)
        const dic = new DicCtor(dicObj, DicCtor.code)
        DcDic.cachedDicMap.set(DicCtor, dic)
        return dic
    }

    static createByOptions(DicCtor, options) {
        if (DcDic.cachedDicMap.has(DicCtor)) {
            return DcDic.cachedDicMap.get(DicCtor)
        }

        const dic = new DicCtor(options, DicCtor.code || DicCtor)
        DcDic.cachedDicMap.set(DicCtor, dic)
        return dic
    }

    /**
     * 获取keyMap中的所有字典
     * @param {Map<string, string>} keyMap Map<key, DictCodeType>
     * @param {*} force
     * @returns Promise<Map<key, DcDic>>
     */
    static async createBy(keyMap, force = false) {
        const keys = Object.keys(keyMap)
        const codes = keys.map(key => keyMap[key])
        const dicts = await this.createMult(codes, force)
        return keys.reduce((acc, key, idx) => {
            acc[key] = dicts[idx]
            return acc
        }, {})
    }

    toName(value, defaultName = null) {
        return this.dict[value] || defaultName
    }

    toNameFn(defaultName = null) {
        const self = this
        const _defaultName = defaultName
        return (value, defaultName) => {
            return self.dict[value] || defaultName || _defaultName
        }
    }

    toValue(name, defaultValue) {
        return this.dictInverse[name] || defaultValue
    }

    toValueFn(defaultValue) {
        const self = this
        const _defaultValue = defaultValue
        return (name, defaultValue) => {
            return self.dictInverse[name] || defaultValue || _defaultValue
        }
    }

    toOptions() {
        return cloneDeep(this.options)
    }
}
